<template>
  <!--
    «Заявка на пополнение» (docs/cabinet-redesign/02 §5.1 В-3): a bank or cash top-up the client filed in the concierge,
    read from its state on every poll — what was asked, how, and where it is now (the request screen's tracker).
  -->
  <div class="cb" data-testid="chat-block-TOPUP_REQUEST">
    <div class="cb__title" data-testid="topup-card-title">{{ t(topUpTitleKey(r?.sourceKind)) }}</div>
    <p v-if="!r" class="cb__text" data-testid="chat-block-state">{{ t('cabinet.topupChat.gone') }}</p>
    <template v-else>
      <div v-if="asked" class="cb__row cb__summary" data-testid="topup-card-amount">
        <!-- Currency first (TARIFF_RATE_MARKUP): «На счёт 3 000,00 EUR» — the sum the client typed, already in their currency. -->
        <span>{{ t(r.requestedAccountAmount != null ? 'vitrine.topup.conv.account'
          : r.requestedCreditAmount != null ? 'cabinet.topupChat.toCard' : 'cabinet.topupChat.toSend') }}</span>
        <strong class="num">{{ f.money(asked.amount, asked.currency) }} {{ asked.currency }}</strong>
      </div>
      <div v-if="r.requestedAccountAmount == null && r.requestedCreditAmount != null && r.amountCurrency" class="cb__row">
        <small>{{ t(forMethod('vitrine.topup.sendCurrency', r.sourceKind)) }}</small>
        <span class="num">{{ r.amountCurrency }}</span>
      </div>
      <div class="cb__row">
        <small>{{ t('cabinet.topupChat.status') }}</small>
        <span data-testid="topup-card-status" :data-status="r.status">{{ t(statusKey) }}</span>
      </div>

      <!-- The request screen's four steps; a closed request shows its status line instead. -->
      <ol v-if="!closed" class="tc-steps" data-testid="topup-card-steps">
        <li v-for="(st, i) in STAGES" :key="st" class="tc-steps__step"
            :class="{ 'tc-steps__step--done': i < step || done, 'tc-steps__step--cur': i === step && !done }"
            :data-testid="`topup-step-${i}`" :data-done="i < step || done ? 'true' : 'false'">
          <span class="tc-steps__mark" aria-hidden="true">
            <svg v-if="i < step || done" viewBox="0 0 24 24"><path d="m5 12 5 5 9-10" /></svg>
          </span>
          <span>{{ t(forMethod(st, r.sourceKind)) }}</span>
        </li>
      </ol>

      <p v-if="!r.payInInstructions && !closed && step < 2" class="cb__hint" data-testid="topup-card-pending">
        {{ t(forMethod('vitrine.topup.payin.pendingBody', r.sourceKind)) }}
      </p>
      <p v-if="r.openedByManager" class="cb__hint">{{ t('vitrine.topup.openedByManager') }}</p>

      <button v-if="r.cancellable" type="button" class="cb__secondary cb__secondary--wide" :disabled="disabled"
              data-testid="topup-card-cancel" @click="cancel">{{ t('vitrine.topup.cancel') }}</button>
    </template>
    <p v-if="error" class="cb__err" data-testid="chat-block-error">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { cancelTopUp } from '@/api/topup';
import type { BlockState } from '@/api/support';
import { STAGES, forMethod, isTerminalStatus, trackerIndex } from '@/views/vitrine/topupStatus';
import { askedAmount, topUpTitleKey } from '@/views/cabinet/concierge/topUpChat';
import { useCardAction, useCardFormat } from './useCardAction';

const props = defineProps<{
  state?: Extract<BlockState, { type: 'TOPUP_REQUEST' }>;
  busy?: boolean;
  reread: () => Promise<void> | void;
}>();

const { t } = useI18n();
const f = useCardFormat();
const { running, error, run } = useCardAction(() => props.reread());

const r = computed(() => props.state?.request ?? null);
const asked = computed(() => (r.value ? askedAmount(r.value) : null));
const done = computed(() => r.value?.status === 'COMPLETED');
const closed = computed(() => !!r.value && isTerminalStatus(r.value.status) && !done.value);
const step = computed(() => trackerIndex(r.value));
const disabled = computed(() => !!props.busy || running.value);
/** The status in the client's words; a code this build does not know reads «в работе». */
const statusKey = computed(() => {
  const s = r.value?.status ?? '';
  const known = ['SUBMITTED', 'SCREENING', 'NEEDS_INFO', 'APPROVED', 'EXPORTED', 'IN_PROGRESS', 'FUNDS_RECEIVED',
    'COMPLETED', 'REJECTED', 'CANCELLED', 'EXPIRED'];
  return `cabinet.topupChat.statusOf.${known.includes(s) ? s : 'IN_PROGRESS'}`;
});

function cancel() {
  const id = r.value?.id;
  if (id) void run(() => cancelTopUp(id));
}
</script>

<style scoped lang="scss">
@use './cards.scss';

.tc-steps { list-style: none; margin: 14px 0 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.tc-steps__step { display: flex; align-items: center; gap: 10px; font-size: 12px; color: var(--muted); }
.tc-steps__mark {
  width: 18px; height: 18px; border-radius: 50%; flex: 0 0 auto; display: grid; place-items: center;
  border: 1px solid var(--line); background: var(--ds-surface);
  svg { width: 12px; height: 12px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
}
.tc-steps__step--done { color: var(--onyx); }
.tc-steps__step--done .tc-steps__mark { border-color: var(--bronze); background: var(--bronze); color: var(--ds-surface); }
.tc-steps__step--cur { color: var(--onyx); font-weight: 500; }
.tc-steps__step--cur .tc-steps__mark { border: 2px solid var(--bronze); }
</style>
